import { MouseEvent, useRef, useState } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faEyeSlash, faPencil } from '@fortawesome/pro-light-svg-icons'

import { Box, Image, StatusBadge } from '@domain-group/suit-theme'
import { useDraggable, useDropTarget } from '@domain-group/react-hooks'

import EnabledPermission from '@components/EnabledPermission'

import { getCampaignType } from '@functions/campaign'

import useImage from '@hooks/useImage'

import { CampaignModel, ImageModel } from '@models/types'

type GetBackgroundColorParams = {
  fadePrivate?: boolean,
  isHovered: boolean,
  isPrivate?: boolean,
  isSelected: boolean,
}

const getBackgroundColor = (params: GetBackgroundColorParams) => {
  const {
    fadePrivate, isHovered, isPrivate, isSelected,
  } = params

  if (isSelected || isHovered) return 'rgba(0, 0, 0, 0.5)'
  if (isPrivate && fadePrivate) return 'rgba(255, 255, 255, 0.7)'
  return null
}

type UseTileDragParams = {
  canReorder?: boolean,
  dropImage: (fromImageId: number) => void,
  id: number,
}

// Interact.js tracks drag position via the transform style and data-x/data-y
// attributes it sets directly on the element - clear them so the next drag starts fresh
const resetDragTransform = (target: HTMLElement) => {
  target.style.webkitTransform = 'translate(0px, 0px)'
  target.style.transform = 'translate(0px, 0px)'
  target.setAttribute('data-x', '0')
  target.setAttribute('data-y', '0')
}

// Makes the tile draggable and a drop target for reordering, via interact.js
const useTileDrag = (params: UseTileDragParams) => {
  const { canReorder, dropImage, id } = params

  const [isDragging, setIsDragging] = useState(false)

  const imageRef = useRef(null)

  // Interact.js only re-registers its callbacks when `canReorder` changes, so read
  // dropImage through a ref to always reorder against the current sort order
  const dropImageRef = useRef(dropImage)
  dropImageRef.current = dropImage

  // The browser still fires a native click after a drag ends - wasDragging() lets
  // the click handler swallow that one click instead of opening the Preview
  const didDragRef = useRef(false)

  const wasDragging = () => {
    if (!didDragRef.current) return false
    didDragRef.current = false
    return true
  }

  useDraggable(imageRef, {
    disabled: !canReorder,
    callbacks: {
      dragStart: () => {
        didDragRef.current = true
        setIsDragging(true)
      },
      // @ts-expect-error TS7006
      moveEnd: (_coords, e) => {
        resetDragTransform(e.target as HTMLElement)
        setIsDragging(false)
        setTimeout(() => { didDragRef.current = false }, 0)
      },
    },
  })

  useDropTarget(imageRef, {}, {
    acceptedTypes: ['photosAndPlansImage'],
    callbacks: {
      // @ts-expect-error TS7006
      dropItem: (data) => {
        const fromImageId = Number(data.dataset.imageId)
        if (fromImageId !== id) dropImageRef.current(fromImageId)
      },
    },
  }, [canReorder])

  return { imageRef, isDragging, wasDragging }
}

interface ImageTileProps {
  anySelected: boolean,
  bulkSelecting: boolean,
  callbacks: {
    dropImage: (fromImageId: number) => void,
    toggleId: (imageId: number) => void,
    selectImage: (id: number) => void,
  },
  campaign: CampaignModel,
  canReorder?: boolean,
  fadePrivate?: boolean,
  hasExportSizeError: boolean,
  hoverActionsEnabled: boolean,
  image: ImageModel,
  selectionIndex: number,
}

const ImageTile = (props: ImageTileProps) => {
  const {
    anySelected, bulkSelecting, callbacks, campaign, canReorder, fadePrivate, hasExportSizeError,
    hoverActionsEnabled, image, selectionIndex,
  } = props

  const { dropImage, toggleId, selectImage } = callbacks

  const [isHovered, setIsHovered] = useState(false)

  const isSelected = selectionIndex >= 0

  const imagePayload = useImage(image)
  const {
    callbacks: { createOrEditImage },
    image: {
      id, id_code, private: isPrivate, original_url, thumb_url,
    },
    isPlan,
    resolution: { label, warn },
  } = imagePayload

  const imageUrl = isPlan ? original_url : thumb_url

  const showHoverState = hoverActionsEnabled && isHovered
  const showCheckCircle = showHoverState && !isSelected
  const showPencil = showCheckCircle && !anySelected

  const tileDragPayload = useTileDrag({ canReorder, dropImage, id })
  const { imageRef, isDragging, wasDragging } = tileDragPayload

  const onClickImage = () => {
    if (wasDragging()) return

    if (bulkSelecting || isSelected) toggleId(id)
    else selectImage(id)
  }

  return (
    <Image
      borderRadius={5}
      boxProps={{
        backgroundSize: isPlan ? 'contain' : 'cover',
        border: '1px solid #ececec',
        onClick: onClickImage,
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        opacity: isDragging ? 0.7 : 1,
      }}
      className={canReorder ? 'draggable' : undefined}
      forwardProps={{ 'data-item-type': 'photosAndPlansImage', 'data-image-id': id }}
      ref={imageRef}
      style={{
        aspectRatio: '4 / 3',
        touchAction: canReorder ? 'none' : 'auto',
        WebkitUserSelect: canReorder ? 'none' : undefined,
        userSelect: canReorder ? 'none' : undefined,
      }}
      url={imageUrl}
      width="100%"
    >
      <Box
        alignItems="center"
        backgroundColor={getBackgroundColor({
          fadePrivate, isHovered: showHoverState, isPrivate, isSelected,
        })}
        borderRadius={5}
        height="100%"
        justifyContent="center"
        position="relative"
        style={{ cursor: canReorder ? 'grab' : 'pointer' }}
      >
        {hasExportSizeError && (
          <StatusBadge
            boxProps={{
              position: 'absolute',
              top: 'medium',
              left: 'medium',
              width: 'auto',
            }}
            color="red"
            text="Error"
          />
        )}

        {!hasExportSizeError && warn && (
          <StatusBadge
            boxProps={{
              position: 'absolute',
              top: 'medium',
              left: 'medium',
              width: 'auto',
            }}
            color={label === 'Low' ? 'red' : 'orange'}
            text={label}
          />
        )}

        {id_code && (
          <StatusBadge
            boxProps={{
              position: 'absolute',
              top: 'medium',
              right: 'medium',
              width: 'auto',
            }}
            color="rgba(0, 0, 0, 0.8)"
            text={id_code}
          />
        )}

        {isPrivate && (
          <StatusBadge
            boxProps={{
              padding: 'small',
              position: 'absolute',
              left: 'medium',
              bottom: 'medium',
              width: 'auto',
            }}
            color="grey"
            icon={<FontAwesomeIcon icon={faEyeSlash} />}
          />
        )}

        {isSelected && (
          <Box
            alignItems="center"
            backgroundColor="green"
            border="2px solid white"
            borderRadius="100%"
            color="white"
            height="40px"
            justifyContent="center"
            onClick={(event: MouseEvent) => {
              event.stopPropagation()
              toggleId(id)
            }}
            style={{ cursor: 'pointer' }}
            width="40px"
          >
            {selectionIndex + 1}
          </Box>
        )}

        {(showCheckCircle || showPencil) && (
          <Box width="auto">
            {showCheckCircle && (
              <Box
                alignItems="center"
                backgroundColor="rgba(0, 0, 0, 0.5)"
                border="2px solid white"
                borderRadius="50%"
                color="white"
                height="36px"
                justifyContent="center"
                marginRight={showPencil ? 'small' : undefined}
                onClick={(event: MouseEvent) => {
                  event.stopPropagation()
                  toggleId(id)
                }}
                style={{ cursor: 'pointer' }}
                width="36px"
              >
                <FontAwesomeIcon icon={faCheck} />
              </Box>
            )}

            {showPencil && (
              <EnabledPermission
                campaignType={getCampaignType(campaign)}
                permissionKey="campaign.media.manage"
              >
                <Box
                  alignItems="center"
                  backgroundColor="rgba(0, 0, 0, 0.5)"
                  border="2px solid white"
                  borderRadius="50%"
                  color="white"
                  height="36px"
                  justifyContent="center"
                  onClick={(event: MouseEvent) => {
                    event.stopPropagation()
                    createOrEditImage()
                  }}
                  style={{ cursor: 'pointer' }}
                  width="36px"
                >
                  <FontAwesomeIcon icon={faPencil} />
                </Box>
              </EnabledPermission>
            )}
          </Box>
        )}
      </Box>
    </Image>
  )
}

export default ImageTile
